export type CryptoPredictionPoint = {
  date: string;
  predictedPrice: number;
  predictedValue: number;
};

type HistoricalPrice = [timestamp: number, price: number];

/**
 * Predict crypto price/value series between start/end dates.
 * - 100 datapoints
 * - Past portion (if start is in the past): uses historical prices (interpolated by UTC day).
 * - Future portion:
 *   - Baseline rules for the FIRST future point:
 *     • If first future point is in the SAME UTC month/year as the last historical date → use currentPrice.
 *     • Else (future month) → predict a baseline from currentPrice (no drift for stablecoins).
 *   - Non-stablecoins: rebase historical multiplicative returns ("as if history started at baseline"),
 *     apply horizon growth, early-step damping, and end-step capping → positive final outcome, no late plunge.
 *   - Stablecoins: no drift (start=end=baseline), keep historical micro-shape with randomized spike placement,
 *     avoid early spikes, and apply mild damping on first steps.
 * - First predictedValue equals the provided amountUSD exactly.
 * - Historical timestamps can be in SECONDS or MILLISECONDS.
 */
export function predictCryptoPriceTrend(
  amountUSD: number | string,
  startDate: Date,
  endDate: Date,
  historicalPrices: HistoricalPrice[]
): CryptoPredictionPoint[] {
  const usdInvestment = Number(amountUSD);
  if (!Number.isFinite(usdInvestment) || usdInvestment <= 0) {
    throw new Error("Investment amount must be a valid positive USD number");
  }
  if (!Array.isArray(historicalPrices) || historicalPrices.length < 20) {
    throw new Error("At least 20 valid [timestamp, price] entries are required.");
  }

  // Historical arrays (assumed sorted ascending by timestamp)
  const histTs = historicalPrices.map((p) => p[0]);
  const histPrices = historicalPrices.map((p) => p[1]);

  // Convert timestamps (seconds OR milliseconds) to UTC day indices
  const histDays = histTs.map((ts) => tsToUTCDay(ts));
  const lastHistDay = histDays[histDays.length - 1];
  const lastHistDateUTC = fromUTCDayCount(lastHistDay);

  // "Today's" price = last historical price (used as "current" reference)
  const currentPrice = histPrices[histPrices.length - 1];

  // Historical stats (for volatility only)
  const histLogReturns = computeLogReturns(histPrices);
  const histVolatility = standardDeviation(histLogReturns);
  const isStablecoinAsset = detectStablecoin(histPrices, histVolatility);
  const projectionVolatility = getProjectionVolatility(histVolatility);

  // --- Target date grid (100 points) using pure UTC day math ---
  const steps = 100;
  const startDay = getUTCDayCount(startDate);
  const endDay = getUTCDayCount(endDate);
  const totalDays = Math.max(0, endDay - startDay);

  const datesUTC: Date[] = [];
  for (let i = 0; i < steps; i++) {
    const dayOffset = Math.round((i * totalDays) / (steps - 1));
    datesUTC.push(fromUTCDayCount(startDay + dayOffset));
  }

  // --- Determine start price for first item (and for holdings) ---
  let startPrice: number;
  if (startDay <= lastHistDay) {
    // Start in the past → precise/interpolated historical price
    startPrice = interpolateHistoricPriceAtDay(startDay, histDays, histPrices);
  } else {
    // Start in the future
    if (isSameUtcMonthYear(fromUTCDayCount(startDay), lastHistDateUTC)) {
      // Start in the current month → use today's price
      startPrice = currentPrice;
    } else {
      // Start in a later month → predict baseline from currentPrice to that future month
      const gapMonths = Math.max(0, diffMonthsUTC(lastHistDateUTC, fromUTCDayCount(startDay)));
      startPrice = predictFutureBaselineFromCurrent(
        currentPrice,
        gapMonths,
        isStablecoinAsset,
        histVolatility,
        histLogReturns
      );
    }
  }

  // Holdings so first predictedValue == amountUSD
  const holdings = usdInvestment / Math.max(startPrice, 1e-9);

  // --- Build prices array: past segment from history; future from baseline(s) ---
  const prices: number[] = new Array(steps);

  // Index where future begins (first date strictly AFTER the last historical day)
  const splitIdx = datesUTC.findIndex((d) => getUTCDayCount(d) > lastHistDay);
  const cutIndex = splitIdx === -1 ? steps : splitIdx;

  // Past portion: use historical interpolation per UTC day
  for (let i = 0; i < cutIndex; i++) {
    const d = getUTCDayCount(datesUTC[i]);
    prices[i] = interpolateHistoricPriceAtDay(d, histDays, histPrices);
  }

  // Future portion
  if (cutIndex < steps) {
    const futureCount = steps - cutIndex;
    const firstFutureDate = datesUTC[cutIndex];

    // Decide baseline AT the cut:
    let futureBaseline: number;
    if (isSameUtcMonthYear(firstFutureDate, lastHistDateUTC)) {
      // First future point is in current month → anchor exactly to currentPrice
      futureBaseline = currentPrice;
    } else {
      // First future point is a later month → predict baseline from currentPrice
      const gapMonthsAtCut = Math.max(0, diffMonthsUTC(lastHistDateUTC, firstFutureDate));
      futureBaseline = predictFutureBaselineFromCurrent(
        currentPrice,
        gapMonthsAtCut,
        isStablecoinAsset,
        histVolatility,
        histLogReturns
      );
    }

    const horizonMonths = Math.max(1, diffMonthsUTC(firstFutureDate, endDate));

    let futurePath: number[];
    if (isStablecoinAsset) {
      // Stablecoin: no drift; start=end=futureBaseline; spikes randomized; no early spikes
      futurePath = generateStablecoinFutureFromHistory(
        futureBaseline,
        histPrices,
        futureCount
      );
      futurePath[0] = futureBaseline; // ensure exact anchor on the first future point
    } else {
      // Crypto: rebase historic returns to baseline ("as if history started at this price")
      futurePath = generateCryptoFutureFromHistory(
        futureBaseline,
        horizonMonths,
        projectionVolatility,
        histVolatility,
        histLogReturns,
        futureCount
      );
      futurePath[0] = futureBaseline;

      // Ensure overall positive outcome vs the ENTIRE start of the range
      const endIdx = futurePath.length - 1;
      if (futurePath[endIdx] < startPrice) {
        futurePath[endIdx] = startPrice;
      }
    }

    // Place future prices into series
    for (let j = 0; j < futureCount; j++) {
      prices[cutIndex + j] = futurePath[j];
    }
  }

  // Ensure the very first price equals startPrice exactly
  prices[0] = startPrice;

  // Map to output points (price → value using fixed holdings)
  const result: CryptoPredictionPoint[] = prices.map((price, idx) =>
    buildDataPoint(datesUTC[idx], price, holdings)
  );

  return result;
}

//
// ── Stablecoin detection ────────────────────────────────────────────────────────
//

function detectStablecoin(prices: number[], histVol: number): boolean {
  const sorted = [...prices].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const median = sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
  const isMedianStable = median > 0.98 && median < 1.02; // USD-pegged band
  const isLowVol = histVol < 0.01; // very low per-step vol
  return isMedianStable || isLowVol;
}

//
// ── UTC date helpers ───────────────────────────────────────────────────────────
//

function getUTCDayCount(date: Date): number {
  return Math.floor(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) / 86400000
  );
}

function fromUTCDayCount(dayCount: number): Date {
  return new Date(dayCount * 86400000);
}

/** Convert UNIX timestamp (seconds OR milliseconds) to UTC day index */
function tsToUTCDay(ts: number): number {
  const ms = ts < 1e12 ? ts * 1000 : ts; // seconds → ms
  const d = new Date(ms);
  return getUTCDayCount(d);
}

function diffMonthsUTC(start: Date, end: Date): number {
  return (end.getUTCFullYear() - start.getUTCFullYear()) * 12 + (end.getUTCMonth() - start.getUTCMonth());
}

function isSameUtcMonthYear(a: Date, b: Date): boolean {
  return a.getUTCFullYear() === b.getUTCFullYear() && a.getUTCMonth() === b.getUTCMonth();
}

//
// ── Historic interpolation (DAY-BASED, exact match aware) ──────────────────────
//

function interpolateHistoricPriceAtDay(
  targetDay: number,
  histDays: number[],
  histPrices: number[]
): number {
  const n = histDays.length;
  const firstDay = histDays[0];
  const lastDay = histDays[n - 1];
  if (targetDay <= firstDay) return histPrices[0];
  if (targetDay >= lastDay) return histPrices[n - 1];

  // Binary search
  let lo = 0;
  let hi = n - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const d = histDays[mid];
    if (d === targetDay) return histPrices[mid];
    if (d < targetDay) lo = mid + 1;
    else hi = mid - 1;
  }

  // Bracket [hi, lo]
  const i0 = Math.max(0, Math.min(hi, n - 2));
  const i1 = Math.min(n - 1, Math.max(lo, 1));
  const d0 = histDays[i0];
  const d1 = histDays[i1];
  const p0 = histPrices[i0];
  const p1 = histPrices[i1];

  if (d1 === d0) return p0; // safety
  const alpha = (targetDay - d0) / (d1 - d0);
  return p0 * (1 - alpha) + p1 * alpha;
}

//
// ── Predict baseline for a future month from currentPrice ──────────────────────
//

function predictFutureBaselineFromCurrent(
  currentPrice: number,
  gapMonths: number,
  isStablecoin: boolean,
  histVol: number,
  histLogReturns: number[]
): number {
  if (gapMonths <= 0 || isStablecoin) return currentPrice;

  // Aggressive but reasonable multiplier by horizon
  const horizonMult = getFinalTargetMultiplier(gapMonths, histVol, false);

  // Add mild volatility flavor using a short normalized path; use only the end
  const steps = Math.max(2, Math.min(12, Math.round(gapMonths))); // 2..12 steps
  const volPath = simulateNormalizedVolatilityFromHistory(steps, histLogReturns, getProjectionVolatility(histVol));
  const curve = Array.from({ length: steps }, (_, i) => 1 + (horizonMult - 1) * (i / (steps - 1)));
  const path: number[] = [];
  for (let i = 0; i < steps; i++) path.push(currentPrice * curve[i] * volPath[i]);

  return Math.max(path[path.length - 1], currentPrice);
}

//
// ── Stablecoin future: historic shape w/ randomized spikes, start=end=base ─────
//

function generateStablecoinFutureFromHistory(
  basePrice: number,
  historyPrices: number[],
  steps: number
): number[] {
  if (steps <= 0) return [];
  if (steps === 1) return [basePrice];

  // Resample history to steps
  const resampled = resampleSeries(historyPrices, steps);
  const stepFactors: number[] = [];
  for (let i = 1; i < resampled.length; i++) {
    const prev = resampled[i - 1];
    const curr = resampled[i];
    const f = prev > 0 ? curr / prev : 1;
    stepFactors.push(f);
  }

  // Identify spikes via 90th percentile of |linear step|
  const linearSteps = stepFactors.map((f) => f - 1);
  const absSteps = linearSteps.map((x) => Math.abs(x));
  const thresh = Math.max(quantile(absSteps, 0.9), 0.002);

  const spikes: number[] = [];
  const baseSteps: number[] = [];
  for (let i = 0; i < stepFactors.length; i++) {
    const ls = linearSteps[i];
    if (Math.abs(ls) >= thresh) {
      spikes.push(stepFactors[i]); // signed spike factor
      baseSteps.push(1.0);         // remove spike from baseline
    } else {
      baseSteps.push(stepFactors[i]);
    }
  }

  // Randomize spike placement (avoid indices 0..1 to prevent early whipsaw)
  shuffleInPlace(spikes);
  const maxAbsFromHist = Math.max(...absSteps, 0.005);
  const positions = randomUniquePositions(spikes.length, baseSteps.length, 2);
  const outSteps = [...baseSteps];
  for (let k = 0; k < spikes.length; k++) {
    const pos = positions[k];
    const lin = spikes[k] - 1;
    const clampedLin = clamp(lin, -maxAbsFromHist, maxAbsFromHist);
    outSteps[pos] = Math.max(0.000001, 1 + clampedLin);
  }

  // Center logs so sum(log f_i) = 0 → ensures start=end
  const logs = outSteps.map((f) => Math.log(Math.max(f, 1e-6)));
  const meanLog = average(logs);
  const centeredLogs = logs.map((l) => l - meanLog);

  // Reconstruct relative path and scale to basePrice
  const rel = [1];
  for (let i = 0; i < centeredLogs.length; i++) rel.push(rel[rel.length - 1] * Math.exp(centeredLogs[i]));
  let series = rel.map((r) => r * basePrice);

  // Gentle damping on first steps (≤±1%, then ≤±1.5%)
  if (series.length >= 2) series[1] = clampStepAround(series[0], series[1], 0.01, 0.01);
  if (series.length >= 3) series[2] = clampStepAround(series[1], series[2], 0.015, 0.015);

  return series;
}

//
// ── Crypto future: REBASED factors + horizon growth + damping + end guards ─────
//

function generateCryptoFutureFromHistory(
  baselinePrice: number,          // ← first-step baseline
  totalMonths: number,
  projectionVolatility: number,
  histVolatility: number,
  histLogReturns: number[],
  steps: number
): number[] {
  if (steps <= 0) return [];
  if (steps === 1) return [baselinePrice];

  // Base target positive end (horizon-driven)
  let targetMultiplier = getFinalTargetMultiplier(totalMonths, histVolatility, false);

  // 1) Rebased volatility path: apply historical multiplicative factors from the baseline
  const stepFactors = sampleFactorsFromHistoricalReturns(histLogReturns, steps, projectionVolatility);
  const prices: number[] = new Array(steps);
  prices[0] = baselinePrice;
  for (let i = 1; i < steps; i++) {
    prices[i] = prices[i - 1] * stepFactors[i - 1];
  }

  // 2) Apply smooth growth curve toward positive target
  const growth = Array.from({ length: steps }, (_, i) =>
    1 + (targetMultiplier - 1) * Math.pow(i / (steps - 1), 1.1)
  );
  for (let i = 0; i < steps; i++) prices[i] *= growth[i];

  // 3) Early-step damping to avoid sharp drop on 2nd point
  const earlyCount = Math.max(3, Math.floor(steps * 0.05));
  if (steps >= 2) prices[1] = clampStepAround(prices[0], prices[1], 0.03, 0.06);      // -3%/+6%
  for (let i = 2; i < Math.min(earlyCount, steps); i++) {
    prices[i] = clampStepAround(prices[i - 1], prices[i], 0.05, 0.08);                // -5%/+8%
  }

  // 4) Raise final target if we already made a big peak earlier (avoid big late drawdown)
  const idx60 = Math.floor(steps * 0.60);
  const idx90 = Math.floor(steps * 0.90);
  const recentPeak = Math.max(...prices.slice(idx60, Math.max(idx90, idx60 + 1)));
  const minFinalVsPeak = recentPeak * 0.98; // finish at least ~98% of recent peak
  let finalTarget = Math.max(baselinePrice * targetMultiplier, baselinePrice, minFinalVsPeak);

  // 5) Final-quarter blend & per-step cap + ascent guardrail (avoid late dip)
  const last = steps - 1;
  const startBlend = Math.floor(steps * 0.75);
  const ratio = finalTarget / prices[last];
  for (let i = startBlend; i < steps; i++) {
    const blend = (i - startBlend) / (steps - startBlend);
    prices[i] = prices[i] * (1 - blend) + (prices[i] * ratio) * blend;
  }

  // Enforce an upward-biased tail with tight drop caps and a smooth guardrail
  enforceFinalAscent(prices, startBlend, finalTarget);

  // Exact final lock
  prices[last] = finalTarget;

  return prices;
}

/** Keep the last quarter trending up (no big last-stage dip). */
function enforceFinalAscent(series: number[], startIdx: number, finalTarget: number): void {
  const n = series.length;
  const endIdx = n - 1;
  if (startIdx >= endIdx) return;

  // Build a smooth, increasing guardrail from current at startIdx to finalTarget
  const startVal = series[startIdx];
  const len = endIdx - startIdx;
  for (let i = startIdx + 1; i <= endIdx; i++) {
    const t = (i - startIdx) / len; // 0..1
    // easeInOutCubic guardrail
    const ease = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    const rail = startVal * Math.pow(finalTarget / startVal, ease);

    // Tight per-step drop cap (e.g., max -2%) and rise cap (e.g., +6%) late in the curve
    const prev = series[i - 1];
    const minAllowed = Math.max(prev * 0.98, rail);
    const maxAllowed = prev * 1.06;

    series[i] = Math.min(maxAllowed, Math.max(minAllowed, series[i]));
  }
}

//
// ── Volatility helpers ─────────────────────────────────────────────────────────
//

function sampleFactorsFromHistoricalReturns(
  histLogReturns: number[],
  steps: number,
  projectionVolatility: number
): number[] {
  const needed = Math.max(0, steps - 1);
  if (needed === 0) return [];

  // Fallback: mild Gaussian if history too short
  if (!histLogReturns || histLogReturns.length < 5) {
    const out: number[] = [];
    const logMean = 0; // drift handled in growth curve
    for (let i = 0; i < needed; i++) {
      const r = randn() * projectionVolatility + logMean;
      out.push(Math.exp(r));
    }
    return out;
  }

  // Block bootstrap
  const len = histLogReturns.length;
  const blockMin = Math.max(3, Math.floor(len * 0.02));
  const blockMax = Math.max(blockMin + 1, Math.floor(len * 0.08));
  const blockLen = randInt(blockMin, blockMax);

  const boot: number[] = [];
  while (boot.length < needed) {
    const s = randInt(0, Math.max(0, len - blockLen));
    boot.push(...histLogReturns.slice(s, s + blockLen));
  }
  const returns = boot.slice(0, needed);

  // Scale to desired projection volatility
  const empStd = standardDeviation(returns);
  const scale = empStd > 0 ? projectionVolatility / empStd : 1;
  const scaled = returns.map((r) => r * scale);

  // Convert to multiplicative factors
  return scaled.map((r) => Math.exp(r));
}

function simulateNormalizedVolatilityFromHistory(
  steps: number,
  histLogReturns: number[],
  projectionVolatility: number
): number[] {
  if (steps <= 0) return [];
  if (!histLogReturns || histLogReturns.length < 5) {
    return simulateNormalizedCryptoVolatility(steps, projectionVolatility);
  }

  const len = histLogReturns.length;
  const blockMin = Math.max(3, Math.floor(len * 0.02));
  const blockMax = Math.max(blockMin + 1, Math.floor(len * 0.08));
  const blockLen = randInt(blockMin, blockMax);

  const needed = steps - 1;
  const boot: number[] = [];
  while (boot.length < needed) {
    const start = randInt(0, Math.max(0, len - blockLen));
    const block = histLogReturns.slice(start, start + blockLen);
    boot.push(...block);
  }
  const returns = boot.slice(0, needed);

  const empStd = standardDeviation(returns);
  const scale = empStd > 0 ? projectionVolatility / empStd : 1;
  const scaled = returns.map((r) => r * scale);

  const series = [1];
  for (let i = 0; i < scaled.length; i++) {
    series.push(series[series.length - 1] * Math.exp(scaled[i]));
  }

  const norm = series[series.length - 1] || 1;
  return series.map((p) => p / norm);
}

function simulateNormalizedCryptoVolatility(steps: number, logStd: number): number[] {
  const logMean = 0; // drift handled elsewhere
  const series: number[] = [1];
  for (let i = 1; i < steps; i++) {
    const logReturn = randn() * logStd + logMean;
    series.push(series[i - 1] * Math.exp(logReturn));
  }
  const norm = series[steps - 1] || 1;
  return series.map((p) => p / norm);
}

//
// ── Stats & helpers ────────────────────────────────────────────────────────────
//

function computeLogReturns(prices: number[]): number[] {
  const out: number[] = [];
  for (let i = 1; i < prices.length; i++) {
    const prev = prices[i - 1];
    const curr = prices[i];
    if (prev > 0 && curr > 0) out.push(Math.log(curr / prev));
  }
  return out;
}

function average(arr: number[]): number {
  if (!arr.length) return 0;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

function standardDeviation(arr: number[]): number {
  if (!arr.length) return 0;
  const m = average(arr);
  const v = arr.reduce((s, x) => s + (x - m) ** 2, 0) / arr.length;
  return Math.sqrt(v);
}

function clamp(x: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, x));
}

function clampStepAround(prev: number, next: number, maxDrop: number, maxRise: number): number {
  const minAllowed = prev * (1 - maxDrop);
  const maxAllowed = prev * (1 + maxRise);
  return Math.max(minAllowed, Math.min(maxAllowed, next));
}

function randInt(lo: number, hi: number): number {
  return Math.floor(Math.random() * (hi - lo + 1)) + lo;
}

function getProjectionVolatility(histVol: number): number {
  const clamped = Math.max(0.001, Math.min(histVol, 0.05));
  if (clamped < 0.01) return 0.01;
  if (clamped < 0.02) return clamped * 0.8;
  return clamped * 1.2;
}

function getFinalTargetMultiplier(totalMonths: number, _histVol: number, isStablecoin: boolean): number {
  const rand = Math.random();
  if (isStablecoin) return 1.0;
  if (totalMonths <= 3) return 1.03 + rand * 0.04;      // +3–7%
  if (totalMonths <= 12) return 1.10 + rand * 0.10;     // +10–20%
  if (totalMonths <= 60) return 1.25 + rand * 0.15;     // +25–40%
  if (totalMonths <= 120) return 1.60 + rand * 0.40;    // +60–100%
  return 2.50 + rand * 1.00;                            // +150–250% for >10y
}

function randn(): number {
  let u = 0, v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
}

function buildDataPoint(date: Date, price: number, holdings: number): CryptoPredictionPoint {
  return {
    date: date.toISOString().split("T")[0],
    predictedPrice: Number(price.toFixed(2)),
    predictedValue: Number((price * holdings).toFixed(2)),
  };
}

// Linear resample to a fixed number of steps
function resampleSeries(values: number[], steps: number): number[] {
  const n = values.length;
  if (steps <= 1) return [values[n - 1]];
  if (n === steps) return [...values];
  const out: number[] = [];
  for (let i = 0; i < steps; i++) {
    const t = i / (steps - 1);
    const pos = t * (n - 1);
    const i0 = Math.floor(pos);
    const i1 = Math.min(n - 1, i0 + 1);
    const frac = pos - i0;
    out.push(values[i0] * (1 - frac) + values[i1] * frac);
  }
  return out;
}

function quantile(arr: number[], q: number): number {
  if (!arr.length) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.floor(q * (sorted.length - 1))));
  return sorted[idx];
}

function randomUniquePositions(count: number, length: number, minIndex = 0): number[] {
  const available: number[] = [];
  for (let i = minIndex; i < length; i++) available.push(i);
  shuffleInPlace(available);
  return available.slice(0, count).sort((a, b) => a - b);
}

function shuffleInPlace<T>(arr: T[]): void {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
}
